import { AxiosResponse } from 'axios';
import { parseISO } from 'date-fns';
import { ChangesMapper } from '../../mappers/change-mappers.ts/changes-mapper';
import {
  Experiment,
  ExperimentAnalyticsSummary,
  ExperimentSettings,
  ExperimentSlim,
} from '../types';

function mapAnalyticsSummary(analytics: any): ExperimentAnalyticsSummary | undefined {
  if (!analytics) return undefined;

  const testVariants = (analytics.variants ?? []).filter((v: any) => v.variantType !== 'control');

  const deltas = {
    cvr: testVariants.length ? Math.max(...testVariants.map((v: any) => v.lift?.cvr ?? 0)) : 0,
    aov: testVariants.length ? Math.max(...testVariants.map((v: any) => v.lift?.aov ?? 0)) : 0,
    arpu: testVariants.length ? Math.max(...testVariants.map((v: any) => v.lift?.arpu ?? 0)) : 0,
  };

  return {
    state: analytics.state,
    daysInTest: analytics.daysInTest ?? 0,
    deltas,
    variantSummary: (analytics.variants ?? []).map((v: any) => ({
      experienceId: v.experienceId,
      variantName: v.variantName,
      variantType: v.variantType,
      users: v.users,
      transactions: v.transactions ?? 0,
      userCvr: v.userCvr,
      lift: v.lift ?? { cvr: 0, aov: 0, arpu: 0 },
      probabilityOfWinning: v.probabilityOfWinning ?? 0,
      isWinner: v.isWinner ?? false,
    })),
  };
}

function parseVariants(variants: [any]) {
  return variants.map((variant: any) => {
    const versionChangesSummary = ChangesMapper.mapChangesSummaryFromResponse(
      variant.versionChangesSummary
    );

    return { ...variant, versionChangesSummary };
  }, []);
}

function parseExperimentDates(experimentRaw: any): Experiment | ExperimentSlim {
  const experiment = { ...experimentRaw };
  ['createdAt', 'updatedAt', 'startedAt', 'endedAt'].forEach(key => {
    if (experiment[key]) {
      experiment[key] = parseISO(experiment[key]);
    }
  });
  if (experiment.variants) {
    experiment.variants = parseVariants(experiment.variants);
  }

  if (experiment.analytics !== undefined) {
    experiment.analyticsSummary = mapAnalyticsSummary(experiment.analytics);
    delete experiment.analytics;
  }

  return experiment;
}

export function mapExperimentsResponse({
  data,
}: AxiosResponse): Array<Experiment | ExperimentSlim> {
  return data.experiments.map((experiment: any) => parseExperimentDates(experiment));
}

export function mapExperimentResponse({ data }: AxiosResponse): Experiment | ExperimentSlim {
  return parseExperimentDates(data);
}

export function mapExperimentSettingsResponse({ data }: AxiosResponse): ExperimentSettings {
  return data;
}

export function mapCreateExperimentResponse({ data }: AxiosResponse): Experiment {
  return parseExperimentDates(data) as Experiment;
}
